// The Ingot Curve: 200 columns, one per lot, each as tall as that lot's real curve price
// (@hashcurve/protocol curve math), so the silhouette is the bonding curve drawn as a pixel
// staircase. Forged lots are ingots in the temper colour of the difficulty they were mined at
// (lib/strip.ts reconstruction); unforged lots are a dotted outline; the next lot carries a
// blinking white-hot caret. Canvas in device pixels with integer column bounds, so it stays crisp
// from a 120 px discovery row to the full-width token page.
import { useEffect, useMemo, useRef, useState } from 'react';
import { LOT, N_LOTS } from '@hashcurve/protocol';
import { useElementWidth, useReducedMotion } from '../hooks/misc';
import { difficultyBits, fmtBits } from '../lib/bits';
import { formatDecimal, formatEth, formatWork } from '../lib/format';
import { LOT_PRICES, lotHeight } from '../lib/lots';
import { lotsIn } from '../lib/params';
import type { Strip, StripCell } from '../lib/strip';
import { temperColor } from '../lib/temper';
import { mix, onFrame } from '../pixel/engine';
import s from './IngotCurve.module.css';

const COL = {
  room: '#1C1830',
  dot: '#6A5C94',
  unknown: '#6E6488',
  white: '#FFF6D8',
  base: '#3B3358',
  baseHi: '#6A5C94',
  fade: '#E0506A',
};

export interface CurveDrawOptions {
  /** index of the next lot (caret), -1 for none */
  next: number;
  /** caret visible this frame */
  caretOn: boolean;
  /** lots cooling from white heat: lot -> 0..1 (1 = just forged) */
  hot?: ReadonlyMap<number, number>;
  /** lots fading out after a sell: lot -> { colour, 0..1 left } */
  fading?: ReadonlyMap<number, { color: string; t: number }>;
  /** a lot to mark (proof card) */
  mark?: number;
  /** device pixel unit for 1-px details */
  u?: number;
  /** draw the stone baseline */
  base?: boolean;
  /** gap between columns in device px (default u) */
  gap?: number;
  /** height in device px of the temper heat strip under the baseline (0 = none) */
  heat?: number;
}

/** Draw the curve into a device-pixel rectangle. Shared by the component and the proof card. */
export function drawIngotCurve(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, cells: readonly StripCell[] | null, o: CurveDrawOptions) {
  const u = o.u ?? 1;
  const baseH = o.base === false ? 0 : 2 * u;
  const heatH = o.heat ?? 0;
  const top = y + 6 * u;
  const bottom = y + h - baseH - heatH;
  const maxH = bottom - top;
  const gap = w / N_LOTS >= 3 * (o.gap ?? u) ? (o.gap ?? u) : 0;
  for (let i = 0; i < N_LOTS; i++) {
    const x0 = x + Math.floor((i * w) / N_LOTS);
    const x1 = x + Math.floor(((i + 1) * w) / N_LOTS);
    const cw = Math.max(1, x1 - x0 - gap);
    const ch = Math.max(2 * u, Math.round(maxH * lotHeight(i)));
    const cy = bottom - ch;
    const c = cells?.[i];
    const hot = o.hot?.get(i);
    const fade = o.fading?.get(i);
    if (hot !== undefined && c && c.difficulty !== null) {
      ingot(ctx, x0, cy, cw, ch, mix(temperColor(c.difficulty), COL.white, hot), u);
    } else if (c && c.fill > 0) {
      const fh = Math.max(u, Math.round(ch * c.fill));
      if (c.difficulty === null) {
        ctx.fillStyle = COL.room;
        ctx.fillRect(x0, cy, cw, ch);
        ctx.fillStyle = COL.unknown;
        for (let yy = bottom - fh; yy < bottom; yy += 2 * u) ctx.fillRect(x0, yy, cw, u);
      } else {
        if (fh < ch) {
          ctx.fillStyle = COL.room;
          ctx.fillRect(x0, cy, cw, ch - fh);
        }
        ingot(ctx, x0, bottom - fh, cw, fh, temperColor(c.difficulty), u);
      }
    } else {
      ctx.fillStyle = COL.room;
      ctx.fillRect(x0, cy, cw, ch);
      if (fade) {
        ctx.fillStyle = fade.color;
        for (let yy = cy; yy < bottom; yy += u) if (((yy - cy) / u + i) % 2 === 0 || fade.t > 0.6) ctx.fillRect(x0, yy, cw, u);
      }
      // dotted outline: the top edge of the room left on the curve
      ctx.fillStyle = COL.dot;
      for (let xx = x0; xx < x0 + cw; xx += u) if (Math.floor(xx / u) % 2 === 0) ctx.fillRect(xx, cy, u, u);
      if (i === N_LOTS - 1 || (cells?.[i + 1]?.fill ?? 0) > 0) for (let yy = cy; yy < bottom; yy += 2 * u) ctx.fillRect(x0 + cw - u, yy, u, u);
    }
    if (o.mark === i) {
      ctx.fillStyle = COL.white;
      ctx.fillRect(x0, cy - 5 * u, cw, 2 * u);
    }
    if (i === o.next && o.caretOn) {
      ctx.fillStyle = COL.white;
      const mid = x0 + Math.floor(cw / 2);
      ctx.fillRect(mid - 2 * u, cy - 5 * u, 5 * u, u);
      ctx.fillRect(mid - u, cy - 4 * u, 3 * u, u);
      ctx.fillRect(mid, cy - 3 * u, u, u);
      ctx.fillRect(x0, cy, cw, u);
      for (let yy = cy; yy < bottom; yy += 2 * u) ctx.fillRect(x0, yy, Math.min(cw, u), u);
    }
  }
  if (baseH) {
    ctx.fillStyle = COL.base;
    ctx.fillRect(x, bottom, w, baseH);
    ctx.fillStyle = COL.baseHi;
    ctx.fillRect(x, bottom, w, u);
  }
  // the heat strip: every lot's temper colour at the same height, so early (short) lots stay legible
  if (heatH) {
    const hy = bottom + baseH;
    for (let i = 0; i < N_LOTS; i++) {
      const x0 = x + Math.floor((i * w) / N_LOTS);
      const x1 = x + Math.floor(((i + 1) * w) / N_LOTS);
      const c = cells?.[i];
      const hot = o.hot?.get(i);
      ctx.fillStyle =
        c && c.fill > 0 ? (c.difficulty === null ? COL.unknown : hot !== undefined ? mix(temperColor(c.difficulty), COL.white, hot) : temperColor(c.difficulty)) : '#141221';
      ctx.fillRect(x0, hy, Math.max(1, x1 - x0), heatH);
    }
  }
}

function ingot(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, color: string, u: number) {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, w, h);
  if (h >= 3 * u) {
    ctx.fillStyle = mix(color, '#FFFFFF', 0.45);
    ctx.fillRect(x, y, w, u);
    ctx.fillStyle = mix(color, '#000000', 0.42);
    ctx.fillRect(x, y + h - u, w, u);
    if (w >= 4 * u) {
      ctx.fillStyle = mix(color, '#000000', 0.22);
      ctx.fillRect(x + w - u, y + u, u, h - 2 * u);
    }
  }
}

const HOT_FRAMES = 14;
const FADE_FRAMES = 10;

/**
 * @param strip  reconstruction from lib/strip.ts (null while loading)
 * @param sold   curve supply in circulation
 * @param size   'large' (token page), 'small' (list rows), 'medium'
 */
export function IngotCurve({
  strip,
  sold,
  size = 'large',
  graduated = false,
  label,
  testId,
}: {
  strip: Strip | null;
  sold: bigint;
  size?: 'large' | 'medium' | 'small';
  graduated?: boolean;
  label?: string;
  testId?: string;
}) {
  const [wrap, width] = useElementWidth<HTMLDivElement>();
  const reduced = useReducedMotion();
  const canvas = useRef<HTMLCanvasElement>(null);
  const cssH = size === 'large' ? 184 : size === 'medium' ? 76 : 34;
  const next = graduated ? -1 : Math.min(N_LOTS, Number(sold / LOT));
  const nextLot = next >= N_LOTS ? -1 : next;

  // animations for lots that changed while we watched
  const hot = useRef(new Map<number, number>());
  const fading = useRef(new Map<number, { color: string; t: number }>());
  const prev = useRef<StripCell[] | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const cells = strip?.cells;
    if (!cells) return;
    const before = prev.current;
    if (before && !reduced) {
      cells.forEach((c, i) => {
        const b = before[i];
        if (!b) return;
        if (c.fill > 0 && c.forgeId && c.forgeId !== b.forgeId) hot.current.set(i, HOT_FRAMES);
        if (c.fill === 0 && b.fill > 0 && b.difficulty !== null) fading.current.set(i, { color: temperColor(b.difficulty), t: FADE_FRAMES });
      });
      if (hot.current.size || fading.current.size) setTick((t) => t + 1);
    }
    prev.current = cells.map((c) => ({ ...c }));
  }, [strip, reduced]);

  const draw = useMemo(
    () => (frame: number) => {
      const el = canvas.current;
      if (!el || !width) return;
      const dpr = Math.max(1, Math.round(window.devicePixelRatio || 1));
      const W = Math.round(width * dpr);
      const H = Math.round(cssH * dpr);
      if (el.width !== W) el.width = W;
      if (el.height !== H) el.height = H;
      const ctx = el.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, W, H);
      const hotT = new Map<number, number>();
      for (const [k, v] of hot.current) hotT.set(k, v / HOT_FRAMES);
      const fadeT = new Map<number, { color: string; t: number }>();
      for (const [k, v] of fading.current) fadeT.set(k, { color: v.color, t: v.t / FADE_FRAMES });
      drawIngotCurve(ctx, 0, 0, W, H, strip?.cells ?? null, {
        next: nextLot,
        caretOn: reduced || size !== 'large' || Math.floor(frame / 5) % 2 === 0,
        hot: hotT,
        fading: fadeT,
        u: size === 'small' ? 1 : size === 'large' ? 2 * dpr : dpr,
        gap: dpr,
        heat: size === 'small' ? 3 * dpr : size === 'large' ? 8 * dpr : 5 * dpr,
        base: size !== 'small',
      });
    },
    [width, cssH, strip, nextLot, reduced, size],
  );

  // large: always animate (caret blink); others: animate only while something cools
  useEffect(() => {
    draw(0);
    if (reduced) return;
    const animate = size === 'large' || hot.current.size > 0 || fading.current.size > 0;
    if (!animate) return;
    let stop: (() => void) | null = null;
    stop = onFrame((f) => {
      for (const [k, v] of hot.current) v <= 1 ? hot.current.delete(k) : hot.current.set(k, v - 1);
      for (const [k, v] of fading.current) v.t <= 1 ? fading.current.delete(k) : (v.t -= 1);
      draw(f);
      if (size !== 'large' && !hot.current.size && !fading.current.size) stop?.();
    });
    return () => stop?.();
  }, [draw, reduced, size, tick]);

  // hover / tap readout
  const [hover, setHover] = useState<number | null>(null);
  const pick = (clientX: number) => {
    const el = canvas.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const i = Math.floor(((clientX - r.left) / r.width) * N_LOTS);
    setHover(i >= 0 && i < N_LOTS ? i : null);
  };

  const summary = label ?? `Ingot curve: ${lotsIn(sold)} of ${N_LOTS} lots in circulation. Column heights are the curve price of each lot.`;
  const interactive = size !== 'small';
  return (
    <div ref={wrap} className={s.wrap} data-size={size} data-testid={testId}>
      <canvas
        ref={canvas}
        className={s.canvas}
        style={{ height: cssH }}
        role="img"
        aria-label={summary}
        data-testid={testId ? undefined : 'temper-strip'}
        onPointerMove={interactive ? (e) => pick(e.clientX) : undefined}
        onPointerDown={interactive ? (e) => pick(e.clientX) : undefined}
        onPointerLeave={interactive ? (e) => e.pointerType === 'mouse' && setHover(null) : undefined}
      />
      {interactive && hover !== null ? <Readout i={hover} cell={strip?.cells[hover]} next={nextLot} width={width} /> : null}
    </div>
  );
}

function Readout({ i, cell, next, width }: { i: number; cell?: StripCell; next: number; width: number }) {
  const p = LOT_PRICES[i]!;
  const left = Math.min(Math.max(0, (i / N_LOTS) * width - 110), Math.max(0, width - 232));
  const status =
    cell && cell.fill > 0
      ? cell.difficulty === null
        ? 'FORGED · HISTORY LOADING'
        : `DIFFICULTY MET ${formatWork(cell.difficulty)} · ${fmtBits(difficultyBits(cell.difficulty))} BITS`
      : i === next
        ? 'NEXT LOT · NEEDS A PROOF'
        : 'NOT FORGED';
  return (
    <div className={s.readout} style={{ left }} role="status" data-testid="ingot-readout">
      <div className={s.readHead}>
        <span>LOT {i + 1}/{N_LOTS}</span>
        {cell && cell.fill > 0 && cell.difficulty !== null ? <span className={s.swatch} style={{ background: temperColor(cell.difficulty) }} /> : null}
      </div>
      <div>
        {formatDecimal((p.perToken / 1e18).toFixed(22), 3)} ETH/token
      </div>
      <div className={s.dim}>lot cost {formatEth(p.cost, 4)} + fee</div>
      <div className={s.status}>{status}</div>
    </div>
  );
}
